import React from 'react'
import styled from 'styled-components'

import type {InvoiceGeneratorFragment} from '@/graphql-types'
import type {ClearError, Errors, SetError} from '@/hooks/use-form-errors'
import {useGlobalStrings} from '@/providers/global-strings'
import {useLocale} from '@/providers/locale'
import {tokens} from '@/theme'
import currencyFormatter from '@/utils/currency-formatter'

import {ReadOnlyInput, SectionWrapper, StyledInput} from './shared-styles'
import {Actions} from './state'
import type {Dispatch, State} from './state'

interface Props {
    dispatch: Dispatch
    state: State
    content: InvoiceGeneratorFragment['data']
    errors: Errors<State>
    setError: SetError<State>
    clearError: ClearError<State>
}

export const Totals = ({dispatch, state, content, errors, setError, clearError}: Props) => {
    const [taxTotal, setTaxTotal] = React.useState(0)
    const [total, setTotal] = React.useState(0)
    const currentLocale = useLocale()
    const globalStrings = useGlobalStrings()
    const formatCurrency = currencyFormatter(
        currentLocale.code,
        state.currency,
        {
            maximumFractionDigits: 2,
        },
        true,
    )

    React.useEffect(() => {
        const taxAmount = state.subtotal * (state.tax / 100)
        setTaxTotal(taxAmount)
        setTotal(state.subtotal + taxAmount)
    }, [state.tax, state.subtotal])

    const onInputChange = (value: string) => {
        dispatch({
            type: Actions.SET_VALUES,
            key: 'tax',
            value: value,
        })

        if (value === '') {
            setError('tax', globalStrings.this_field_is_required)
            return
        }

        clearError('tax')
    }

    return (
        <Wrapper>
            <ReadOnlyInput label={'Subtotal'} value={formatCurrency(state.subtotal)} readOnly />
            <StyledInput
                label={content.tax_label}
                value={state.tax || ''}
                type="number"
                error={errors['tax']}
                onChange={onInputChange}
            />
            <ReadOnlyInput
                label={content.tax_total_label}
                value={formatCurrency(taxTotal)}
                readOnly
            />

            <ReadOnlyInput label={content.total_label} value={formatCurrency(total)} readOnly />
        </Wrapper>
    )
}

const Wrapper = styled(SectionWrapper)`
    > *:not(:first-child) {
        margin-top: ${tokens.spacing20};
    }
`
